import { Grid, Card, CardHeader, CardContent, CardActions, Button, Typography, IconButton, useTheme, Tooltip } from '@mui/material';
import EditRounded from '@mui/icons-material/EditRounded';
import DeleteRounded from '@mui/icons-material/DeleteRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import styled from '@emotion/styled';
import SimplePagination from '../../../utils/utilities/SimplePagination';

import { Link } from 'react-router-dom';

import { CardBlock, CardBlockHeader, CardBlockContainer, CardSpacer } from '../../../utils/utilities/CardComponents';

import useSimpleTable from '../../../hooks/useSimpleTable';

import { usePageData } from '../../../contexts/PageData';
import { BasicFlex, LabelUpper } from '../../../utils/Common';

import { formatGenericDate, formatCurrency } from '../../../utils/util';

import DownloadButton from '../../../utils/utilities/Buttons/DownloadButton';
import AttachEmailRoundedIcon from '@mui/icons-material/AttachEmailRounded';

import MultiStatusChip from '../../../utils/utilities/MultiStatusChip';

import useIsMobile from '../../../hooks/useIsMobile';

const InvoiceList = styled(BasicFlex)`
  margin-bottom: 1rem;
`;

const InvoiceCardStyles = styled(Card)`

`;

const ActionsBarStyles = styled(BasicFlex)`
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 6px;
  overflow: hidden;
`;

const ActionItem = styled(BasicFlex)`
  width: 3.5rem;
  height: 3.5rem;
  background-color: ${props => props.$bg ? props.$bg : props.theme.colors.primaryFaint};
  align-items: center;
  justify-content: center;
  border-right: 1px solid ${props => props.theme.colors.border};
  cursor: pointer;

  &:last-child {
    border-right: none;
  }
`;

const LinkItem = styled(Link)`
  display: flex;
  width: 3.5rem;
  height: 3.5rem;
  background-color: ${props => props.$bg ? props.$bg : props.theme.colors.primaryFaint};
  align-items: center;
  justify-content: center;
  border-right: 1px solid ${props => props.theme.colors.border};
  cursor: pointer;

  &:last-child {
    border-right: none;
  }
`;

const Status = styled.div`
  font-size: 1.2rem;
`

const ActionsBar = ({ invoice }) => {
  const theme = useTheme();
  return (
    <ActionsBarStyles>
      <LinkItem $bg={theme.palette.info.faint} to={`/invoices/view/${invoice.invoice_id}`}>
        <Tooltip title="View Invoice"><SearchRoundedIcon size="small" color="info" /></Tooltip>
      </LinkItem>
      <ActionItem $bg={theme.palette.success.faint}>
        <DownloadButton url={`/invoices/generate/${invoice.invoice_id}`} title="Print Invoice" />
      </ActionItem>
      <ActionItem $bg={theme.palette.primary.faint}>
        <Tooltip title="Email Invoice"><AttachEmailRoundedIcon size="small" color="primary" /></Tooltip>
      </ActionItem>
      <ActionItem $bg={theme.palette.secondary.faint}>
        <Tooltip title="Edit Invoice"><EditRounded size="small" color="secondary" /></Tooltip>
      </ActionItem>
      <ActionItem $bg={theme.palette.error.faint}>
        <Tooltip title="Delete Invoice"><DeleteRounded size="small" color="error" /></Tooltip>
      </ActionItem>
    </ActionsBarStyles>
  )
}

const InvoiceCard = ({invoice}) => {
  const totalBlock = [
    { header: "Total", info: invoice.subtotal, dataType: "currency" },
    { header: "Paid", info: invoice.total_paid, dataType: "currency" },
    { header: "Balance", info: invoice.balance, dataType: "currency" },
  ]
  const { isMobile } = useIsMobile();
  return (
    <InvoiceCardStyles variant="outlined" sx={{width: "100%", backgroundColor: "#fcfcfc"}}>
      <CardHeader 
      title={<BasicFlex justify="flex-start" gap={isMobile ? "2rem" : "5rem"} align="flex-start">
        <div>Invoice #{invoice.invoice_id}</div>
        <Status>
        <MultiStatusChip value={invoice.paid_status} values={[{value: "unpaid", label: "Unpaid", status: "error"}, {value: "partially_paid", label: "Partially Paid", status: "warning"}, {value: "paid", label: "Paid", status: "success"}]} /></Status>
        </BasicFlex>}
      subheader={`${formatGenericDate(invoice.date)}`} 
      action={<ActionsBar invoice={invoice} />}
      slotProps={{title: {sx: {fontSize: isMobile ? "1.4rem" : "auto"}}, subheader: {sx: {fontSize: isMobile ? "1.2rem" : "auto"}}}}
      />
      <CardContent sx={{py: 1}}>
        <BasicFlex justify="flex-start" gap="5rem" align="flex-start">
          <BasicFlex dir="column" width="auto" gap=".2rem">
            <LabelUpper $size="1.2rem">Subtotal</LabelUpper>
            <div>{formatCurrency(invoice.subtotal)}</div>
          </BasicFlex>
          <BasicFlex dir="column" width="auto" gap=".2rem">
            <LabelUpper $size="1.2rem">Total</LabelUpper>
            <div>{formatCurrency(invoice.total)}</div>
          </BasicFlex>
          <BasicFlex dir="column" width="auto" gap=".2rem">
            <LabelUpper $size="1.2rem">Balance</LabelUpper>
            <div>{formatCurrency(invoice.balance)}</div>
          </BasicFlex>
          {/* <BasicFlex dir="column" width="auto">
            <LabelUpper $size="1.2rem">Status</LabelUpper>
            <MultiStatusChip value={invoice.paid_status} values={[{value: "unpaid", label: "Unpaid", status: "error"}, {value: "partially_paid", label: "Partially Paid", status: "warning"}, {value: "paid", label: "Paid", status: "success"}]} />
          </BasicFlex> */}
        </BasicFlex>
      </CardContent>
    </InvoiceCardStyles>
  )
}

const Invoices = () => {

  const { data } = usePageData() || {};

  const { data: invoices, pagination, setPagination } = useSimpleTable({url: `/invoices/customer/${data.customer_id}`});
  console.log(invoices, "data");
  return (
    <Grid container spacing={3}>
      <Grid size={{xs: 12, md: 8}}>
        <Card elevation={0}>
          <CardHeader title="Invoices" sx={{p: 1}} />

          <CardContent sx={{p: 1}}>
            <InvoiceList dir="column" gap="1.5rem">
              {invoices?.rows?.map((invoice) => (
                <InvoiceCard key={invoice.invoice_id} invoice={invoice} />
              ))}
            </InvoiceList>
            <SimplePagination 
              rowsPerPage={pagination?.rowsPerPage} 
              currentPage={pagination?.page} 
              setPagination={setPagination} 
              resultCount={invoices?.count} 
              results={invoices?.rows} 
            />
          </CardContent>

        </Card>
      </Grid>

      <Grid size={{xs: 12, md: 4}}>
        <Card variant='outlined'>
          <CardHeader title="Balance History" sx={{p: 1}} />
          
        </Card>
      </Grid>

    </Grid>
  )
};

export default Invoices;